import React, {useContext, useEffect, useState, useRef} from "react";
import {azureTokenContext, userIdContext} from "../../App";
import * as ApiCalls from "./ApiCalls.js";
import {NewsItem} from "../../Types/NewsItem";
import {useLogMetric} from "../../hooks/useLogMetric";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import "../../style/adminMessagesBanner.css";

function sortNewsByPriority(news: NewsItem[]): NewsItem[] {
    return [...news].sort((a, b) => {
        const priorityA = a.Priority ?? Number.MAX_SAFE_INTEGER;
        const priorityB = b.Priority ?? Number.MAX_SAFE_INTEGER;
        if (priorityA !== priorityB) return priorityA - priorityB;
        const dateA = a.PublishTime ? new Date(a.PublishTime).getTime() : 0;
        const dateB = b.PublishTime ? new Date(b.PublishTime).getTime() : 0;
        return dateB - dateA;
    });
}

export default function AdminMessagesBanner() {
    const azureToken = useContext(azureTokenContext);
    const userId = useContext(userIdContext);
    const logMetric = useLogMetric();

    const [news, setNews] = useState<NewsItem[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isMinimized, setIsMinimized] = useState(false);
    const [contentVisible, setContentVisible] = useState(true);
    const [loading, setLoading] = useState(true);
    const [animating, setAnimating] = useState(false);
    const [slideDirection, setSlideDirection] = useState<"left" | "right">("left");
    const slideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const expandTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        ApiCalls.getPublishedNewsByDestination("AuthorsPortal", azureToken)
            .then((res: NewsItem[]) => {
                setNews(sortNewsByPriority(res));
            })
            .catch(async (err: Error) => {
                console.error("Get published news by destination failed.", err);
                ApiCalls.log("ERROR", "Failed getPublishedNewsByDestination: " + err.message, "AuthorsPortal", "", "", userId, azureToken);
            })
            .finally(() => {
                setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [azureToken]);

    // Clean up timeouts on unmount
    useEffect(() => {
        return () => {
            if (slideTimeoutRef.current) clearTimeout(slideTimeoutRef.current);
            if (expandTimeoutRef.current) clearTimeout(expandTimeoutRef.current);
        };
    }, []);

    if (loading || news.length === 0) return null;

    const currentMessage = news[currentIndex];

    const handleNext = () => {
        if (animating) return;
        setSlideDirection("left");
        setAnimating(true);
        if (slideTimeoutRef.current) clearTimeout(slideTimeoutRef.current);
        slideTimeoutRef.current = setTimeout(() => {
            setCurrentIndex((prev) => (prev + 1) % news.length);
            setAnimating(false);
        }, 200);
        logMetric({category: "AdminMessages", subCategory: "Navigate next", message: "User navigated to next admin message"});
    };

    const handlePrev = () => {
        if (animating) return;
        setSlideDirection("right");
        setAnimating(true);
        if (slideTimeoutRef.current) clearTimeout(slideTimeoutRef.current);
        slideTimeoutRef.current = setTimeout(() => {
            setCurrentIndex((prev) => (prev - 1 + news.length) % news.length);
            setAnimating(false);
        }, 200);
        logMetric({category: "AdminMessages", subCategory: "Navigate prev", message: "User navigated to previous admin message"});
    };

    const handleMinimize = () => {
        setIsMinimized(true);
        setContentVisible(false);
        logMetric({category: "AdminMessages", subCategory: "Minimized", message: "User minimized admin messages banner"});
    };

    const handleExpand = () => {
        setIsMinimized(false);
        // Delay body/controls until width animation is mostly done
        if (expandTimeoutRef.current) clearTimeout(expandTimeoutRef.current);
        expandTimeoutRef.current = setTimeout(() => setContentVisible(true), 100);
        logMetric({category: "AdminMessages", subCategory: "Expanded", message: "User expanded admin messages banner"});
    };

    return (
        <div className="admin-messages-banner-wrapper">
            <div
                className={`admin-messages-banner ${isMinimized ? "admin-messages-banner--minimized" : ""}`}
                role={isMinimized ? "button" : "region"}
                aria-label={isMinimized ? "Vis administratormeldinger" : "Administratormeldinger"}
                tabIndex={isMinimized ? 0 : undefined}
                onClick={isMinimized ? handleExpand : undefined}
                onKeyDown={isMinimized ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleExpand();
                    }
                } : undefined}
            >
                {/* Yellow accent bar - only visible when expanded */}
                <div className="admin-messages-accent-bar" />

                {/* Expanded content */}
                <div className="admin-messages-expanded-content" {...(isMinimized ? {inert: ""} as any : {})}>
                    {!isMinimized && (
                    <div className="admin-messages-content">
                        <div className="admin-messages-bell">
                            <NotificationsIcon className="admin-messages-bell-icon" />
                        </div>

                        <div className="admin-messages-text">
                            <div
                                className={`admin-messages-text-inner ${animating ? `admin-messages-slide-out-${slideDirection}` : "admin-messages-slide-in"}`}
                            >
                                <h3 className="admin-messages-title">{currentMessage.Title}</h3>
                                <div className={`admin-messages-body-revealer ${contentVisible ? "admin-messages-body-revealer--visible" : ""}`}>
                                    <div className="admin-messages-body-revealer-inner">
                                        <div className="admin-messages-body-scroll">
                                            <div
                                                className="admin-messages-body"
                                                dangerouslySetInnerHTML={{__html: currentMessage.Content ?? ''}}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={`admin-messages-controls ${contentVisible ? "admin-messages-controls--visible" : ""}`}>
                            {news.length > 1 && (
                                <div className="admin-messages-pagination">
                                    <button
                                        onClick={handlePrev}
                                        className="admin-messages-nav-btn"
                                        aria-label="Forrige melding"
                                    >
                                        <ChevronLeftIcon fontSize="small" />
                                    </button>
                                    <span className="admin-messages-counter">
                                        {currentIndex + 1} / {news.length}
                                    </span>
                                    <button
                                        onClick={handleNext}
                                        className="admin-messages-nav-btn"
                                        aria-label="Neste melding"
                                    >
                                        <ChevronRightIcon fontSize="small" />
                                    </button>
                                </div>
                            )}
                            <div className="admin-messages-divider" />
                            <button
                                onClick={handleMinimize}
                                className="admin-messages-minimize-btn"
                                aria-label="Minimer meldinger"
                            >
                                <ExpandLessIcon />
                            </button>
                        </div>
                    </div>
                    )}
                </div>
                <div className="admin-messages-minimized-content">
                    <NotificationsIcon className="admin-messages-bell-icon-small" />
                    <span className="admin-messages-minimized-text">
                        Vis meldinger ({news.length})
                    </span>
                    <ExpandMoreIcon className="admin-messages-expand-icon" />
                </div>
            </div>
        </div>
    );
}